/**
 * 用户头像组件
 *
 * 显示用户头像，支持图片加载失败时自动显示姓名首字母，基于名字自动生成背景颜色。
 */

import type { AvatarProps } from 'antd';
import { Avatar, theme } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';

/**
 * 预设颜色池
 */
const AVATAR_COLORS = [
  '#1677ff',
  '#52c41a',
  '#fa8c16',
  '#722ed1',
  '#13c2c2',
  '#eb2f96',
  '#fa541c',
  '#2f54eb',
  '#faad14',
  '#a0d911',
  '#1890ff',
  '#f5222d',
  '#9254de',
  '#36cfc9',
  '#ff7a45',
  '#597ef7',
];

/**
 * 根据名字生成稳定的颜色索引
 */
function getColorByName(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash; // Convert to 32bit integer
  }
  const index = Math.abs(hash) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
}

/**
 * 获取显示的缩写文字
 * - 中文：取最后两个字
 * - 英文：取首字母大写
 * - 混合：取第一个字符
 */
function getInitials(name: string): string {
  if (!name) return '?';
  const trimmed = name.trim();
  if (!trimmed) return '?';

  // 中文名：取最后 1-2 个字
  const chineseChars = trimmed.match(/[\u4e00-\u9fff]/g);
  if (chineseChars && chineseChars.length >= 2) {
    return chineseChars.slice(-2).join('');
  }
  if (chineseChars && chineseChars.length === 1) {
    return chineseChars[0];
  }

  // 英文名：取各单词首字母（最多2个）
  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }

  // 单个单词：取第一个字符
  return trimmed[0].toUpperCase();
}

/**
 * UserAvatar 组件属性
 */
export interface UserAvatarProps extends Omit<AvatarProps, 'src' | 'children'> {
  /** 头像图片 URL */
  src?: string | null;
  /** 用户名（用于生成缩写和颜色） */
  name?: string;
  /** 自定义 fallback 文字 */
  fallbackText?: string;
  /** 自定义 fallback 颜色 */
  fallbackColor?: string;
}

/**
 * 用户头像组件
 *
 * @example
 * ```tsx
 * // 有头像图片
 * <UserAvatar src="/avatars/user1.jpg" name="张三" size={40} />
 *
 * // 无头像 -> 自动显示名字缩写 + 颜色
 * <UserAvatar name="张三" size={40} />
 * // 显示 "三" 并自动分配背景色
 *
 * // 英文名
 * <UserAvatar name="John Doe" size={40} />
 * // 显示 "JD"
 * ```
 */
export const UserAvatar = React.memo(function UserAvatar({
  src,
  name = '',
  fallbackText,
  fallbackColor,
  style,
  ...restProps
}: UserAvatarProps): React.ReactElement {
  const { token } = theme.useToken();
  const [imgError, setImgError] = useState(false);

  // 当 src 变化时重置错误状态，确保新图片可以正常加载
  useEffect(() => {
    setImgError(false);
  }, [src]);

  const initials = useMemo(
    () => fallbackText ?? getInitials(name),
    [name, fallbackText],
  );
  const bgColor = useMemo(
    () => fallbackColor ?? getColorByName(name),
    [name, fallbackColor],
  );

  const showImage = src && !imgError;

  if (showImage) {
    return (
      <Avatar
        src={src}
        onError={() => {
          setImgError(true);
          return true;
        }}
        style={style}
        {...restProps}
      />
    );
  }

  return (
    <Avatar
      style={{
        backgroundColor: bgColor,
        color: token.colorTextLightSolid,
        fontWeight: 500,
        ...style,
      }}
      {...restProps}
    >
      {initials}
    </Avatar>
  );
});
